import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface ProjectFormProps {
  initialData?: {
    id?: string;
    title: string;
    year: number;
    description?: string;
    url?: string;
    technologies: string[];
  };
  onSave: (data: {
    title: string;
    year: number;
    description?: string;
    url?: string;
    technologies: string[];
  }) => void;
  onCancel: () => void;
  onDelete?: () => void;
  skillTerms: { id: string; term: string }[];
}

const ProjectForm = ({ initialData, onSave, onCancel, onDelete, skillTerms }: ProjectFormProps) => {
  const [title, setTitle] = useState(initialData?.title || "");
  const [year, setYear] = useState<number>(initialData?.year || new Date().getFullYear());
  const [description, setDescription] = useState(initialData?.description || "");
  const [url, setUrl] = useState(initialData?.url || "");
  const [technologies, setTechnologies] = useState<string[]>(initialData?.technologies || []);
  const [newTechnology, setNewTechnology] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [errors, setErrors] = useState<{
    title?: string;
    year?: string;
    description?: string;
    technologies?: string;
  }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!title.trim()) {
      newErrors.title = "Project title is required";
    }
    if (!year) {
      newErrors.year = "Year is required";
    }
    if (!description?.trim()) {
      newErrors.description = "Description is required";
    }
    if (technologies.length === 0) {
      newErrors.technologies = "At least one technical skill is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    onSave({
      title,
      year,
      description,
      url,
      technologies,
    });
  };

  const handleAddTechnology = (tech: string) => {
    if (!technologies.includes(tech)) {
      setTechnologies([...technologies, tech]);
      setErrors(prev => ({ ...prev, technologies: undefined }));
    }
    setNewTechnology("");
    setShowSuggestions(false);
  };

  const handleRemoveTechnology = (tech: string) => {
    setTechnologies(technologies.filter((t) => t !== tech));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="title" className="text-sm font-medium mb-1 block">
          Project title <span className="text-red-500">*</span>
        </Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setErrors(prev => ({ ...prev, title: undefined }));
          }}
          placeholder="E-commerce Platform"
          className={cn(errors.title && "border-red-500")}
        />
        {errors.title && (
          <p className="text-sm text-red-500 mt-1">{errors.title}</p>
        )}
      </div>

      <div>
        <Label htmlFor="year" className="text-sm font-medium mb-1 block">
          Year <span className="text-red-500">*</span>
        </Label>
        <Input
          id="year"
          type="number"
          value={year}
          onChange={(e) => {
            setYear(parseInt(e.target.value));
            setErrors(prev => ({ ...prev, year: undefined }));
          }}
          className={cn(errors.year && "border-red-500")}
          placeholder={(new Date().getFullYear()).toString()}
        />
        {errors.year && (
          <p className="text-sm text-red-500 mt-1">{errors.year}</p>
        )}
      </div>

      <div>
        <Label htmlFor="url" className="text-sm font-medium mb-1 block">
          Project link
        </Label>
        <Input
          id="url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
        />
      </div>

      <div>
        <Label className="text-sm font-medium mb-1 block">
          Technical skills used <span className="text-red-500">*</span>
        </Label>
        <div className="relative" ref={dropdownRef}>
          <Input
            value={newTechnology}
            onChange={(e) => {
              setNewTechnology(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Add technology..."
            className={cn(errors.technologies && "border-red-500")}
          />
          {showSuggestions && (
            <div className="absolute w-full z-10">
              <Command className="rounded-lg border shadow-md">
                <CommandList>
                  <CommandEmpty>No skill found.</CommandEmpty>
                  <CommandGroup className="max-h-[200px] overflow-auto">
                    {skillTerms
                      .filter((term) =>
                        term.term.toLowerCase().includes(newTechnology.toLowerCase())
                      )
                      .map((term) => (
                        <CommandItem
                          key={term.id}
                          value={term.term}
                          onSelect={handleAddTechnology}
                          className="cursor-pointer"
                        >
                          {term.term}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {technologies.map((tech) => (
            <Badge
              key={tech}
              variant="secondary"
              className="cursor-pointer group"
              onClick={() => handleRemoveTechnology(tech)}
            >
              {tech} <span className="group-hover:text-red-500 pl-1">×</span>
            </Badge>
          ))}
        </div>
        {errors.technologies && (
          <p className="text-sm text-red-500 mt-1">{errors.technologies}</p>
        )}
      </div>

      <div>
        <Label htmlFor="description" className="text-sm font-medium mb-1 block">
          Description <span className="text-red-500">*</span>
        </Label>
        <textarea
          id="description"
          className={cn(
            "w-full min-h-[100px] p-2 border rounded-md",
            errors.description && "border-red-500"
          )}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setErrors(prev => ({ ...prev, description: undefined }));
          }}
          placeholder="Describe your project..."
        />
        {errors.description && (
          <p className="text-sm text-red-500 mt-1">{errors.description}</p>
        )}
        <div className="text-sm text-gray-500 mt-1">
          {description.length} / 2000
        </div>
      </div>

      <div className="flex justify-between">
        <div>
          {initialData?.id && onDelete && (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
        <div className="space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            Save
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ProjectForm;